import { useState } from 'react';
import axios from 'axios';

// Session AG (2 Sep 2026) — forgotten-password reset screen.
//
// Reached at /reset/<token> from the emailed link. Mounted by an early return
// in App.js on the pathname, NOT by a <Routes> entry: when nobody is logged in
// App renders <AuthPage /> and the router is never reached, so a Route here
// would be dead code. Same pattern as /join/<code> in AuthPage.
//
// ⚠️ This is NOT first login. SetPassword.js handles must_change_password —
// a different flow, a different route, and it also records POPIA consent.
// This screen changes ONE thing: the password. It deliberately does NOT log
// the person in and does NOT return a token; they sign in normally afterwards,
// which keeps every account on the same login path — including imported staff
// who still owe their consent acceptance at the first-login interstitial.
//
// ⚠️ The backend answers a bad or expired token with 400, never 401. The axios
// interceptor in AuthContext logs the user out on any 401 except the login
// call, so a 401 here would sign out whoever is logged in on that device just
// for opening a stale link.
export default function ResetPassword() {
  // The token is the last path segment. Read once at module render — there is
  // no navigation within this screen.
  const token = window.location.pathname.split('/').filter(Boolean)[1] || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match. Please try again.'); return; }
    setSaving(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset your password. Please request a new link.');
      setSaving(false);
    }
  };

  const Logo = () => (
    <div className="auth-logo">
      <h1>
        <span style={{color:'var(--text-primary)',fontWeight:800}}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
        <span style={{color:'var(--accent)',fontWeight:800}}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
      </h1>
    </div>
  );

  // A link with no token at all — someone truncated the URL, or a mail client
  // mangled it. Say so plainly rather than failing on submit.
  if (!token) {
    return (
      <div className="auth-screen">
        <div className="auth-box">
          <Logo />
          <div style={{textAlign:'center',padding:'8px 0 24px'}}>
            <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
            <div style={{fontWeight:700,fontSize:18,marginBottom:10,color:'var(--text-primary)'}}>Invalid reset link</div>
            <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:24}}>
              This link is incomplete. Please request a new one from the sign-in page.
            </div>
            <button className="btn btn-primary" onClick={() => window.location.replace('/')}>
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth-screen">
        <div className="auth-box">
          <Logo />
          <div style={{textAlign:'center',padding:'8px 0 24px'}}>
            <div style={{fontSize:40,marginBottom:16}}>✓</div>
            <div style={{fontWeight:700,fontSize:18,marginBottom:10,color:'var(--text-primary)'}}>Password updated</div>
            <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:24}}>
              You can now sign in with your new password.
            </div>
            {/* replace(), not href: drops the token URL from history so the
                dead link is not sitting in the back button on a shared phone. */}
            <button className="btn btn-primary" onClick={() => window.location.replace('/')}>
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-box">
        <Logo />

        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{fontSize:32,marginBottom:10}}>🔑</div>
          <div style={{fontWeight:700,fontSize:17,marginBottom:8,color:'var(--text-primary)'}}>Choose a new password</div>
          <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6}}>
            Enter a new password for your account. You'll use it every time you log in.
          </div>
        </div>

        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" placeholder="Min. 8 characters"
              value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" placeholder="Re-enter your new password"
              value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} />
          </div>

          {error && <p style={{color:'var(--danger)',fontSize:13,marginBottom:12}}>{error}</p>}

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Set New Password'}
          </button>
        </form>

        <div className="auth-switch">
          Changed your mind?{' '}
          <button type="button" onClick={() => window.location.replace('/')}>
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
